import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { MetricsApi, MonthlySupplierMetricsResponse, QUERY_KEYS } from '@api'
import { useDashboardFilters, useUser } from '@contexts'

import { useCarriers } from '../useCarriers'
import { useSuppliers } from '../useSuppliers'
import { MONTHLY_COMPLIANCE_STATUS_ORDER } from '../../features/AdminRouter/AdminDashboardMetrics/monthlyComplianceStatus'
import { mergeDailyData } from './useDashboardData'

const useCompanyGmtZone = () => {
  const { state } = useUser()
  return state?.company?.gmtZone ?? 0
}

export function useWeeklyMetrics() {
  const { weekStart, tripType, supplierId, carrierId } = useDashboardFilters()
  const gmtZone = useCompanyGmtZone()

  return useQuery(
    [QUERY_KEYS.METRICS_WEEKLY, weekStart.toISOString(), tripType, gmtZone, supplierId, carrierId],
    () => MetricsApi.getWeekly({ weekStart, tripType, gmtZone, supplierId, carrierId }),
    {
      refetchOnWindowFocus: true,
    },
  )
}

export function useDailyMetrics() {
  const { selectedDay, tripType, supplierId, carrierId } = useDashboardFilters()
  const gmtZone = useCompanyGmtZone()

  return useQuery(
    [QUERY_KEYS.METRICS_DAILY, selectedDay.toISOString(), tripType, gmtZone, supplierId, carrierId],
    () =>
      MetricsApi.getDaily({
        date: selectedDay,
        tripType: [tripType],
        gmtZone,
        supplierId,
        carrierId,
      }),
    {
      refetchOnWindowFocus: true,
      enabled: !!selectedDay,
    },
  )
}

export function useMonthlyCompliance() {
  const { selectedMonth, tripType, monthlyComplianceStatuses, supplierId, carrierId } =
    useDashboardFilters()
  const gmtZone = useCompanyGmtZone()

  const complianceStatuses = useMemo(
    () =>
      monthlyComplianceStatuses.length > 0
        ? monthlyComplianceStatuses
        : [...MONTHLY_COMPLIANCE_STATUS_ORDER],
    [monthlyComplianceStatuses],
  )

  return useQuery(
    [
      QUERY_KEYS.METRICS_MONTHLY,
      selectedMonth.toISOString(),
      tripType,
      complianceStatuses.join(','),
      gmtZone,
      supplierId,
      carrierId,
    ],
    () =>
      MetricsApi.getMonthlyCompliance({
        monthStart: selectedMonth,
        tripType: [tripType],
        complianceStatuses,
        gmtZone,
        supplierId,
        carrierId,
      }),
    {
      refetchOnWindowFocus: true,
    },
  )
}

export function useMonthlyComplianceByZone() {
  const { selectedMonth, tripType, monthlyComplianceStatuses, supplierId, carrierId } =
    useDashboardFilters()
  const gmtZone = useCompanyGmtZone()

  const complianceStatuses = useMemo(
    () =>
      monthlyComplianceStatuses.length > 0
        ? monthlyComplianceStatuses
        : [...MONTHLY_COMPLIANCE_STATUS_ORDER],
    [monthlyComplianceStatuses],
  )

  return useQuery(
    [
      QUERY_KEYS.METRICS_MONTHLY_COMPLIANCE_BY_ZONE,
      selectedMonth.toISOString(),
      tripType,
      complianceStatuses.join(','),
      gmtZone,
      supplierId,
      carrierId,
    ],
    () =>
      MetricsApi.getMonthlyComplianceByZone({
        monthStart: selectedMonth,
        tripType: [tripType],
        complianceStatuses,
        gmtZone,
        supplierId,
        carrierId,
      }),
    {
      refetchOnWindowFocus: true,
    },
  )
}

export function useMonthlySupplierMetrics() {
  const { selectedMonth, tripType, monthlyComplianceStatuses, supplierId, carrierId } =
    useDashboardFilters()
  const gmtZone = useCompanyGmtZone()

  const complianceStatuses = useMemo(
    () =>
      monthlyComplianceStatuses.length > 0
        ? monthlyComplianceStatuses
        : [...MONTHLY_COMPLIANCE_STATUS_ORDER],
    [monthlyComplianceStatuses],
  )

  const metricsQuery = useQuery(
    [
      QUERY_KEYS.METRICS_MONTHLY_SUPPLIERS,
      selectedMonth.toISOString(),
      tripType,
      complianceStatuses.join(','),
      gmtZone,
      supplierId,
      carrierId,
    ],
    () =>
      MetricsApi.getMonthlySuppliers({
        monthStart: selectedMonth,
        tripType: [tripType],
        complianceStatuses,
        gmtZone,
        supplierId,
        carrierId,
      }),
    {
      refetchOnWindowFocus: true,
    },
  )

  const { suppliers } = useSuppliers({ disabled: false })
  const { carriers } = useCarriers({ disabled: false, limit: 1000 })

  const suppliersForMerge = useMemo(() => {
    if (!suppliers) return suppliers
    if (supplierId !== undefined) return suppliers.filter(s => s.id === supplierId)
    if (carrierId !== undefined) return []
    return suppliers
  }, [suppliers, supplierId, carrierId])

  const carriersForMerge = useMemo(() => {
    if (!carriers || carrierId === undefined) return carriers
    return carriers.filter(c => c.id === carrierId)
  }, [carriers, carrierId])

  const data = useMemo(() => {
    const merged = mergeDailyData(
      metricsQuery.data ? { suppliers: metricsQuery.data.suppliers } : undefined,
      suppliersForMerge,
      carriersForMerge,
    )
    return { suppliers: merged.suppliers } as MonthlySupplierMetricsResponse
  }, [metricsQuery.data, suppliersForMerge, carriersForMerge])

  return {
    data,
    isLoading: metricsQuery.isLoading,
    isFetching: metricsQuery.isFetching,
  }
}

export { useDashboardData } from './useDashboardData'
